import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Lock, QrCode, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { startPayment } from "@/lib/paytm";
import { TrustBadges } from "@/components/TrustBadges";
import { Logo } from "@/components/Logo";
import { metaContext, track } from "@/lib/meta";
import { oppref } from "@/lib/oaiq";

const ICONS = { upi: QrCode, netbanking: Landmark };
const inr = (n) => `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Checkout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planKey = params.get("plan");
  const credits = Number(params.get("credits") || 0);

  const [catalogue, setCatalogue] = useState(null);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setCatalogue(r.data));
    api.get("/billing/paytm/methods").then((r) => {
      setMethods(r.data.methods || []);
      setMethod((r.data.methods?.[0] || {}).key || "");
    });
  }, []);

  const order = useMemo(() => {
    if (!catalogue) return null;
    if (planKey) {
      const plan = catalogue.plans.find((p) => p.key === planKey);
      if (!plan) return null;
      return {
        title: `Propojo ${plan.label} subscription`,
        period: plan.months === 1 ? "1 month" : plan.months === 3 ? "3 months" : "12 months",
        meta: `${plan.credits} proposals · ${plan.seats} seats`,
        total: plan.amount_inr,
        payload: { plan: plan.key },
      };
    }
    const qty = Math.max(25, Math.min(5000, credits || 25));
    return {
      title: `${qty} proposal credits`,
      period: "One-time top-up",
      meta: `1 credit = 1 proposal · ${inr(catalogue.credit_price_inr)} per credit`,
      total: qty * catalogue.credit_price_inr,
      payload: { quantity: qty },
    };
  }, [catalogue, planKey, credits]);

  const pay = async () => {
    if (busy) return;
    setBusy(true);
    const eventId = `apay_${Date.now()}`;
    track("AddPaymentInfo", {
      currency: "INR", value: order.total, content_name: order.title,
      content_ids: [planKey || "credits"], payment_mode: method,
    }, { eventId });
    try {
      const { order_id } = await startPayment({ ...order.payload, mode: method, meta: metaContext(eventId), oppref: oppref() });
      navigate(`/payment/${order_id}`, { replace: true });
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!order) return;
    track("InitiateCheckout", {
      currency: "INR", value: order.total, content_name: order.title,
      content_ids: [planKey || "credits"], num_items: 1,
    }, { eventId: `ic_${planKey || `credits${credits}`}_${Math.floor(Date.now() / 60000)}` });
  }, [order?.title]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!catalogue || !order)
    return (
      <div className="min-h-screen grid place-items-center bg-app text-zinc-600">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen nv-grid-bg text-zinc-100">
      <div className="mx-auto max-w-3xl px-5 py-12">
        <div className="flex items-center gap-3">
          <Logo size={28} withWordmark />
          <button
            data-testid="checkout-back-btn"
            onClick={() => navigate("/plans")}
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to plans
          </button>
        </div>

        <p className="mt-10 text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">Secure checkout</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">Review and pay.</h1>

        <div className="mt-8 grid md:grid-cols-[1.15fr_1fr] gap-4 items-start">
          <div data-testid="order-summary-card" className="nv-card p-5">
            <p className="text-[11px] font-num uppercase tracking-[0.18em] text-zinc-500">Order summary</p>
            <p data-testid="order-title" className="mt-3 text-lg font-semibold tracking-tight">{order.title}</p>
            <p className="text-xs text-zinc-500">{order.period} · {order.meta}</p>

            <div className="mt-5 pt-4 border-t border-zinc-800 flex justify-between items-baseline">
              <span className="text-sm font-semibold">Total payable</span>
              <span data-testid="order-total" className="text-3xl font-extrabold font-num tracking-tight">{inr(order.total)}</span>
            </div>
            <p className="mt-3 text-[11px] text-zinc-500">
              All taxes included. An invoice with your order number is issued the moment the payment is confirmed.
            </p>
          </div>

          <div className="nv-card p-5">
            <p className="text-[11px] font-num uppercase tracking-[0.18em] text-zinc-500">Payment method</p>
            <div className="mt-3 space-y-2">
              {methods.map((m) => {
                const Icon = ICONS[m.key] || Lock;
                const active = method === m.key;
                return (
                  <button
                    key={m.key}
                    data-testid={`pay-method-${m.key}-option`}
                    onClick={() => setMethod(m.key)}
                    className={`w-full text-left rounded-xl border px-3.5 py-3 flex items-start gap-3 transition-colors ${
                      active ? "border-indigo-500/50 bg-indigo-500/10" : "border-zinc-800 hover:border-zinc-700"
                    }`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-indigo-300" : "text-zinc-500"}`} />
                    <span className="flex-1">
                      <span className="block text-sm font-medium">{m.label}</span>
                      <span className="block text-[11px] text-zinc-500">{m.hint}</span>
                    </span>
                    {active && <Check className="w-4 h-4 text-indigo-300 shrink-0" />}
                  </button>
                );
              })}
              {methods.length === 0 && (
                <p className="text-xs text-zinc-500">Loading available payment methods…</p>
              )}
            </div>

            <button
              data-testid="pay-now-btn"
              onClick={pay}
              disabled={busy || !method}
              className="mt-5 w-full h-12 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-medium inline-flex items-center justify-center gap-2 transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
              {busy ? "Opening secure window…" : `Pay ${inr(order.total)} securely`}
            </button>
            <p className="mt-2 text-[11px] text-center text-zinc-500">
              You will complete the payment in the gateway's secure window.
            </p>
          </div>
        </div>

        <TrustBadges className="mt-4" />
      </div>
    </div>
  );
}
